import memoize from 'lodash/memoize';

import { cleanTag } from './helpers';
import { path } from './media';
import { getLandmarkRatingData } from './article';
import config from '~/config';

// See https://developers.google.com/search/docs/data-types/product for specifications
const getTripJson = memoize(
  trip => {
    const reviews = trip.reviews || [];
    const wo = trip.travelers_without_review || 0;
    const count = reviews.length + wo;
    const jsonLd = {
      '@type': 'Product',
      name: cleanTag(trip.name),
      description: cleanTag(trip.introduction || trip.custom_detail || ''),
      image: [
        path(trip.cover_image, 'square-300'),
        path(trip.cover_image, 'rectangle-800-600'),
        path(trip.cover_image, 'hd')
      ],
      brand: 'TakeMeTour',
      offers: {
        '@type': 'Offer',
        priceCurrency: 'THB',
        price: (trip.full_prices[1] || trip.full_prices[0]).displayPrice,
        availability: 'http://schema.org/InStock',
        url: `${config.baseURL.en}/${trip.product_type === 'ticket' ? 'ticket' : 'trip'}/${trip.slug}`
      },
    };
    if (trip.user_id.rating && trip.user_id.rating.average_rating >= 1 && count > 0) {
      jsonLd.aggregateRating = {
        '@type': 'AggregateRating',
        ratingValue: trip.user_id.rating.average_rating.toFixed(1),
        reviewCount: count.toString(),
      };
    }
    return jsonLd;
  },
  trip => trip._id
);


const companyJson = {
  '@type': 'Organization',
  url: 'https://www.takemetour.com',
  logo: `${config.assetPath}/static/images/master-logo.png`,
  name: 'TakeMeTour',
};

const getArticleImageJson = (imagePath) => ({
  '@type': 'ImageObject',
  url: path(imagePath, 'rectangle-600-600'),
  height: 600,
  width: 600,
});

const getArticleJson = (article) => {
  const jsonLd = {
    '@type': 'Article',
    headline: article.title,
    image: getArticleImageJson(article.thumbnail_image || article.banner_image),
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://www.takemetour.com/article'
    },
    author: companyJson,
    datePublished: article.published_at,
    dateModified: article.modified_at,
    publisher: companyJson,
  };
  return jsonLd;
};

const getLandmarkJson = (article) => {
  const jsonLd = {
    '@type': 'LocalBusiness',
    name: article.title,
    image: getArticleImageJson(article.thumbnail_image || article.banner_image),
  };
  const { averageRating, ratingCount, reviewCount } = getLandmarkRatingData(article);
  if (!!averageRating) {
    jsonLd.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: averageRating,
      ratingCount,
      ...(reviewCount > 0 ? { reviewCount } : {}),
    };
  }
  return jsonLd;
};

/**
 * getJsonLd
 * @param {String} key
 * @param {*} param
 * @returns {{}}
 */
export const getJsonLd = (key, param, withContextKey = true) => {
  let jsonLd = withContextKey ? { '@context': 'http://schema.org/' } : {};
  switch (key) {
    case 'getTrip':
      jsonLd = { ...jsonLd, ...getTripJson(param) };
      break;
    case 'company':
      jsonLd = { ...jsonLd, ...companyJson };
      break;
    case 'getArticle':
      jsonLd = { ...jsonLd, ...getArticleJson(param) };
      break;
    case 'getLandmark':
      jsonLd = { ...jsonLd, ...getLandmarkJson(param) };
      break;
    default: break;
  }
  return jsonLd;
};

/**
 * getJsonLds
 * @param {{ key: String, param: * }[]} keyParams
 * @returns {{}[]}
 */
export const getJsonLds = (keyParams) => {
  return {
    '@context': 'http://schema.org/',
    '@graph': keyParams.map(keyParam => getJsonLd(keyParam.key, keyParam.param, false))
  };
};
